/**
 * AttachmentDropdown
 *
 * A dropdown menu that provides different attachment options for the chat interface
 *
 * Primary responsibilities:
 * - Display upload file option
 * - Display attach image option
 * - Display take screenshot option (desktop only)
 * - Handle file selection and screenshot capture
 */

import { FC, useRef, useState } from 'react'
import {
  IconPaperclip,
  IconPhoto,
  IconCameraSelfie,
  IconPlus,
  IconMusic
} from '@tabler/icons-react'
import { toast } from 'sonner'

import { cn } from '@/lib/utils'
import useSafeTranslation from '@/hooks/useSafeTranslation'
import {
  getTryAttachmentOverLimitDescription,
  isOverTryAttachmentLimit,
  TRY_USER_MAX_ATTACHMENT_LABEL
} from '@/lib/chat/try-temp-attachments'

import { Button } from '../ui/button'
import { PublicLoginPrompt } from './public-login-prompt'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip'
import { Input } from '../ui/input'

interface AttachmentDropdownProps {
  filesToAccept: string
  onFileSelect: (file: File) => void
  onFilesSelect?: (files: File[]) => void | Promise<void>
  isUploading?: boolean
  isAuthenticated?: boolean
  isPublic?: boolean
  chatId?: string
  /** False when the selected model's LLM entry has imageInput: false (e.g. GLM, DeepSeek) — disables the image/screenshot options instead of letting a send fail server-side. */
  imagesSupported?: boolean
}

export const AttachmentDropdown: FC<AttachmentDropdownProps> = ({
  filesToAccept,
  onFileSelect,
  onFilesSelect,
  isUploading = false,
  isAuthenticated = true,
  isPublic = false,
  chatId,
  imagesSupported = true
}) => {
  const { t } = useSafeTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const audioInputRef = useRef<HTMLInputElement>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)
  const [loginPromptDescription, setLoginPromptDescription] = useState<string | undefined>()

  // Try users can attach images/text up to 1 MB without signing in.
  const isTryMode = isPublic && !isAuthenticated

  const isMobileOrTablet =
    typeof navigator !== 'undefined' &&
    /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)

  const openLoginPrompt = (description: string) => {
    setLoginPromptDescription(description)
    setShowLoginPrompt(true)
    setIsOpen(false)
  }

  const acceptTrySizedFiles = (files: File[]): File[] => {
    if (!isTryMode) return files

    const allowed: File[] = []
    let blockedName: string | undefined

    for (const file of files) {
      if (isOverTryAttachmentLimit(file.size)) {
        blockedName = blockedName || file.name
        continue
      }
      allowed.push(file)
    }

    if (blockedName) {
      openLoginPrompt(getTryAttachmentOverLimitDescription(blockedName))
    }

    return allowed
  }

  const dispatchSelectedFiles = (files: File[]) => {
    if (files.length === 0) return

    if (onFilesSelect) {
      void onFilesSelect(files)
      return
    }

    files.forEach(file => {
      onFileSelect(file)
    })
  }

  const handleFileUpload = () => {
    fileInputRef.current?.click()
    setIsOpen(false)
  }

  const handleImageUpload = () => {
    if (!imagesSupported) {
      setIsOpen(false)
      return
    }
    imageInputRef.current?.click()
    setIsOpen(false)
  }

  const handleAudioUpload = () => {
    if (isTryMode) {
      openLoginPrompt(
        'Audio needs an account for transcription and larger uploads. Log in to attach audio.'
      )
      return
    }
    audioInputRef.current?.click()
    setIsOpen(false)
  }

  const handleNativeAttachment = () => {
    fileInputRef.current?.click()
  }

  const handleScreenshot = async () => {
    setIsOpen(false)

    if (!imagesSupported) {
      return
    }

    if (isTryMode) {
      openLoginPrompt('Log in to capture and attach screenshots.')
      return
    }

    try {
      if (window.electronAPI?.captureAndUploadScreenshot) {
        const result = await window.electronAPI.captureAndUploadScreenshot({
          type: 'screen',
          uploadOptions: {
            source: 'chat',
            ...(chatId ? { chatId } : {})
          }
        })

        if (result?.cancelled) {
          return
        }

        if (result?.permissionRequired) {
          throw new Error(result.message || result.error || 'Screen capture permission is required')
        }

        if (result?.error) {
          throw new Error(result.error)
        }

        const attachments = Array.isArray(result?.attachments)
          ? result.attachments.filter(
              (attachment): attachment is Record<string, unknown> =>
                Boolean(attachment) && typeof attachment === 'object'
            )
          : []

        if (attachments.length === 0) {
          throw new Error('No screenshot was captured')
        }

        for (const attachment of attachments) {
          const signedUrl = String(
            attachment.signedUrl || attachment.signed_url || attachment.url || ''
          )
          if (!signedUrl) {
            throw new Error('Captured screenshot is missing a signed URL')
          }

          const response = await fetch(signedUrl)
          if (!response.ok) {
            throw new Error('Failed to load captured screenshot')
          }

          const blob = await response.blob()
          const file = new File(
            [blob],
            String(attachment.name || `screenshot-${Date.now()}.png`),
            {
              type:
                String(attachment.mimeType || attachment.type || '') ||
                blob.type ||
                'image/png'
            }
          )
          onFileSelect(file)
        }
        return
      }

      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: {
          mediaSource: 'screen'
        } as MediaStreamConstraints['video']
      })

      const video = document.createElement('video')
      video.srcObject = stream
      video.play()

      video.addEventListener('loadedmetadata', () => {
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight

        const ctx = canvas.getContext('2d')
        if (!ctx) {
          stream.getTracks().forEach(track => track.stop())
          return
        }

        ctx.drawImage(video, 0, 0)
        stream.getTracks().forEach(track => track.stop())

        canvas.toBlob(blob => {
          if (blob) {
            const file = new File([blob], `screenshot-${Date.now()}.png`, {
              type: 'image/png'
            })
            onFileSelect(file)
          }
        }, 'image/png')
      })
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Could not capture screenshot'
      )
      fileInputRef.current?.click()
    }
  }

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || files.length === 0) return

    dispatchSelectedFiles(acceptTrySizedFiles(Array.from(files)))
    event.target.value = ''
  }

  const loginPrompt = (
    <PublicLoginPrompt
      open={showLoginPrompt}
      onOpenChange={open => {
        setShowLoginPrompt(open)
        if (!open) setLoginPromptDescription(undefined)
      }}
      feature="larger uploads"
      description={loginPromptDescription}
    />
  )

  const hiddenFileInputs = (
    <div className="hidden">
      <Input
        ref={fileInputRef}
        type="file"
        onChange={handleFileChange}
        accept={filesToAccept}
        multiple
      />
      <Input
        ref={imageInputRef}
        type="file"
        onChange={handleFileChange}
        accept="image/*"
        multiple
      />
      <Input
        ref={audioInputRef}
        type="file"
        onChange={handleFileChange}
        accept="audio/*"
        multiple
      />
    </div>
  )

  if (isMobileOrTablet) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className={cn(
                'hover:bg-accent rounded-lg p-2 transition-colors',
                isUploading && 'cursor-not-allowed opacity-50'
              )}
              disabled={isUploading}
              onClick={handleNativeAttachment}
              aria-label={t('Attach files')}
            >
              <IconPlus size={20} className="text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>
              {isTryMode
                ? `Attach files (up to ${TRY_USER_MAX_ATTACHMENT_LABEL})`
                : t('Attach files')}
            </p>
          </TooltipContent>
        </Tooltip>
        {hiddenFileInputs}
        {loginPrompt}
      </TooltipProvider>
    )
  }

  return (
    <TooltipProvider>
      <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'hover:bg-accent rounded-lg p-2 transition-colors',
                  isUploading && 'cursor-not-allowed opacity-50'
                )}
                disabled={isUploading}
                aria-label={t('Attach files')}
              >
                <IconPlus size={20} className="text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent side="top">
            <p>{t('Attach files')}</p>
          </TooltipContent>
        </Tooltip>
        <DropdownMenuContent align="start" className="min-w-[180px]" sideOffset={5} side="top">
          <DropdownMenuLabel>{t('Attach')}</DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 py-2"
            onClick={handleFileUpload}
          >
            <IconPaperclip size={16} className="text-muted-foreground" />
            <div className="flex flex-1 flex-col">
              <span className="text-sm">{t('Upload file')}</span>
              {isTryMode && (
                <span className="text-muted-foreground text-xs">
                  Up to {TRY_USER_MAX_ATTACHMENT_LABEL}
                </span>
              )}
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem
            className={cn(
              'flex items-center gap-2 py-2',
              imagesSupported ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
            )}
            disabled={!imagesSupported}
            onClick={handleImageUpload}
          >
            <IconPhoto size={16} className="text-muted-foreground" />
            <div className="flex flex-1 flex-col">
              <span className="text-sm">{t('Attach image')}</span>
              {!imagesSupported ? (
                <span className="text-muted-foreground text-xs">Not supported by this model</span>
              ) : (
                isTryMode && (
                  <span className="text-muted-foreground text-xs">
                    Up to {TRY_USER_MAX_ATTACHMENT_LABEL}
                  </span>
                )
              )}
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem
            className="flex cursor-pointer items-center gap-2 py-2"
            onClick={handleAudioUpload}
          >
            <IconMusic size={16} className="text-muted-foreground" />
            <div className="flex flex-1 flex-col">
              <span className="text-sm">{t('Attach audio')}</span>
              {isTryMode && (
                <span className="text-muted-foreground text-xs">Log in required</span>
              )}
            </div>
          </DropdownMenuItem>

          <DropdownMenuItem
            className={cn(
              'flex items-center gap-2 py-2',
              imagesSupported ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
            )}
            disabled={!imagesSupported}
            onClick={handleScreenshot}
          >
            <IconCameraSelfie size={16} className="text-muted-foreground" />
            <div className="flex flex-1 flex-col">
              <span className="text-sm">{t('Take screenshot')}</span>
              {!imagesSupported ? (
                <span className="text-muted-foreground text-xs">Not supported by this model</span>
              ) : (
                isTryMode && (
                  <span className="text-muted-foreground text-xs">Log in required</span>
                )
              )}
            </div>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {loginPrompt}
      {hiddenFileInputs}
    </TooltipProvider>
  )
}
